import axios from 'axios';
import CONFIG, { getApiUrl } from '../../../utils/config';
import { getUserInfo, getAuthHeader, getUserId, isAdmin } from '../../../services/authService';

// Interface cho User
export interface User {
  id: number;
  username: string;
  stravaId: string | null;
  roles: string[] | null;
  createdAt: string | null;
}

// Interface cho ClubMember
export interface ClubMember {
  id: number;
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  profileImageUrl?: string | null;
  gender?: string | null;
  role?: string;
  active?: boolean;
  stravaAthleteId?: string | null;
  hasStravaToken?: boolean;
  stravaConnected?: boolean;
  stravaTokenExpireDate?: number[] | null;
  createdAt?: string | number[];
  updatedAt?: string | number[];
  
  // Các trường cũ để đảm bảo tương thích
  user?: User;
  firstname?: string;
  lastname?: string;
  membership?: string;
  isAdmin?: boolean;
  isOwner?: boolean;
  isFromActivity?: boolean;
}

// Interface cho Club Activity
export interface ClubActivity {
  id: number;
  memberId: number;
  memberName: string;
  activityId: string;
  activityName: string;
  type: string;
  distance: number;
  movingTime: number;
  elapsedTime: number;
  totalElevationGain: number;
  startDate: string;
  startDateLocal: string;
  timezone: string;
  utcOffset: number;
  mapPolyline: string | null;
}

// Interface cho Team Report
export interface TeamReport {
  teamId: number;
  teamName: string;
  totalActivities: number;
  totalDistance: number;
  totalMovingTime: number;
  totalElevationGain: number;
  memberStats: {
    memberId: number;
    memberName: string;
    activitiesCount: number;
    totalDistance: number;
    totalMovingTime: number;
    totalElevationGain: number;
  }[];
  activityTypesStats: {
    [key: string]: {
      count: number;
      distance: number;
      movingTime: number;
      elevationGain: number;
    };
  };
}

// Định nghĩa interface cho dữ liệu trả về
export interface Team {
  id: number;
  name: string;
  description?: string;
  imageUrl?: string | null;
  tournamentId?: number;
  tournamentName?: string;
  captainId?: number;
  captainName?: string;
  memberCount?: number;
  active?: boolean;
  createdAt?: string | number[];
  updatedAt?: string | number[];
}

// Interface cho ApiResponse
interface ApiResponse<T> {
  result: string;
  message: string;
  data: T;
}

// Interface cho việc tạo team mới
export interface CreateTeamRequest {
  name: string;
  description: string;
  tournamentId?: number;
  imageUrl?: string;
  captainId?: number;
}

// Interface cho việc cập nhật team
export interface UpdateTeamRequest {
  name?: string;
  description?: string;
  tournamentId?: number;
  imageUrl?: string;
}

// Hàm lấy tất cả các team
export const getAllTeams = async (): Promise<Team[]> => {
  try {
    const authHeader = getAuthHeader();
    
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xem danh sách đội');
    }
    
    const response = await axios.get(getApiUrl('/api/teams'), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    if (response.data.result === 'SUCCESS') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Không thể lấy danh sách đội');
    }
  } catch (error: any) {
    console.error('Lỗi khi lấy tất cả đội:', error);
    throw new Error(error.response?.data?.message || error.message || 'Có lỗi xảy ra');
  }
};

// Hàm lấy team theo userId
export const getTeamsByUserId = async (userId: number): Promise<Team[]> => {
  try {
    const authHeader = getAuthHeader();
    
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xem danh sách đội');
    }
    
    const response = await axios.get(getApiUrl(`/api/teams/user/${userId}`), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    if (response.data.result === 'SUCCESS') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Không thể lấy danh sách đội của bạn');
    }
  } catch (error: any) {
    console.error('Lỗi khi lấy dữ liệu nhóm của người dùng:', error);
    throw new Error(error.response?.data?.message || error.message || 'Có lỗi xảy ra');
  }
};

// Hàm lấy team theo ID
export const getTeamById = async (teamId: number): Promise<Team> => {
  try {
    const authHeader = getAuthHeader();
    
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xem thông tin đội');
    }
    
    const response = await axios.get(getApiUrl(`/api/teams/${teamId}`), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    if (response.data.result === 'SUCCESS') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Không thể lấy thông tin đội');
    }
  } catch (error: any) {
    console.error(`Lỗi khi lấy đội ID ${teamId}:`, error);
    throw new Error(error.response?.data?.message || error.message || 'Có lỗi xảy ra');
  }
};

// Hàm lấy team theo tournament ID
export const getTeamsByTournamentId = async (tournamentId: number): Promise<Team[]> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xem thông tin đội');
    }
    
    const response = await axios.get<ApiResponse<Team[]>>(getApiUrl(`/api/teams/tournament/${tournamentId}`), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    return response.data.data;
  } catch (error) {
    console.error(`Lỗi khi lấy team theo tournament ID ${tournamentId}:`, error);
    throw error;
  }
};

// Hàm lấy team theo admin ID
export const getTeamsByAdminId = async (adminId: number): Promise<Team[]> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xem thông tin đội');
    }
    
    const response = await axios.get<ApiResponse<Team[]>>(getApiUrl(`/api/teams/admin/${adminId}`), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    return response.data.data;
  } catch (error) {
    console.error(`Lỗi khi lấy team theo admin ID ${adminId}:`, error);
    throw error;
  }
};

// Hàm lấy các team mà thành viên tham gia
export const getTeamsByMemberId = async (memberId: number): Promise<Team[]> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xem thông tin đội');
    }
    
    const response = await axios.get<ApiResponse<Team[]>>(getApiUrl(`/api/teams/member/${memberId}`), {
      headers: {
        'Authorization': authHeader
      }
    });
    
    return response.data.data;
  } catch (error) {
    console.error(`Lỗi khi lấy team của thành viên ID ${memberId}:`, error);
    throw error;
  }
};

// Hàm tạo team mới
export const createTeam = async (teamData: CreateTeamRequest): Promise<Team> => {
  try {
    const authHeader = getAuthHeader();
    
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để tạo đội');
    }
    
    // Kiểm tra quyền admin
    if (!isAdmin()) {
      throw new Error('Bạn không có quyền tạo đội. Chỉ quản trị viên mới có quyền này.');
    }
    
    // Lấy userId của người dùng đang đăng nhập
    const userId = getUserId();
    if (!userId) {
      throw new Error('Không thể xác định thông tin người dùng');
    }
    
    // Thêm captainId vào dữ liệu gửi đi nếu chưa có
    const teamRequestData = {
      ...teamData,
      captainId: teamData.captainId || userId
    };
    
    console.log('Gửi dữ liệu tạo đội:', teamRequestData);
    
    const response = await axios.post(getApiUrl('/api/teams'), teamRequestData, {
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.data.result === 'SUCCESS') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Không thể tạo đội');
    }
  } catch (error: any) {
    console.error('Lỗi khi tạo đội mới:', error);
    throw new Error(error.response?.data?.message || error.message || 'Có lỗi xảy ra');
  }
};

// Hàm cập nhật thông tin team
export const updateTeam = async (teamId: number, teamData: UpdateTeamRequest): Promise<Team> => {
  try {
    const authHeader = getAuthHeader();
    
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để cập nhật đội');
    }
    
    // Kiểm tra quyền admin
    if (!isAdmin()) {
      throw new Error('Bạn không có quyền cập nhật đội. Chỉ quản trị viên mới có quyền này.');
    }
    
    const response = await axios.put(getApiUrl(`/api/teams/${teamId}`), teamData, {
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json'
      }
    });
    
    if (response.data.result === 'SUCCESS') {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Không thể cập nhật đội');
    }
  } catch (error: any) {
    console.error(`Lỗi khi cập nhật đội ID ${teamId}:`, error);
    throw new Error(error.response?.data?.message || error.message || 'Có lỗi xảy ra');
  }
};

// Hàm xóa team
export const deleteTeam = async (teamId: number): Promise<void> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xóa đội');
    }
    
    // Kiểm tra quyền admin
    if (!isAdmin()) {
      throw new Error('Bạn không có quyền xóa đội. Chỉ quản trị viên mới có quyền này.');
    }
    
    await axios.delete<ApiResponse<null>>(getApiUrl(`/api/teams/${teamId}`), {
      headers: {
        'Authorization': authHeader
      }
    });
  } catch (error) {
    console.error(`Lỗi khi xóa team ID ${teamId}:`, error);
    throw error;
  }
};

// Hàm thêm thành viên vào team
export const addMemberToTeam = async (teamId: number, userId: number): Promise<Team> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để thêm thành viên');
    }
    
    const response = await axios.post<ApiResponse<Team>>(
      getApiUrl(`/api/teams/${teamId}/members`),
      {
        userId: userId,
        role: "MEMBER"
      },
      {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.data;
  } catch (error) {
    console.error(`Lỗi khi thêm thành viên ${userId} vào team ${teamId}:`, error);
    throw error;
  }
};

// Hàm xóa thành viên khỏi team
export const removeMemberFromTeam = async (teamId: number, userId: number): Promise<Team> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xóa thành viên');
    }
    
    const response = await axios.delete<ApiResponse<Team>>(
      getApiUrl(`/api/teams/${teamId}/members/${userId}`),
      {
        headers: {
          'Authorization': authHeader
        }
      }
    );
    
    return response.data.data;
  } catch (error) {
    console.error(`Lỗi khi xóa thành viên ${userId} khỏi team ${teamId}:`, error);
    throw error;
  }
};

// Hàm lấy danh sách thành viên trong team
export const getTeamMembers = async (teamId: number): Promise<ClubMember[]> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xem danh sách thành viên');
    }
    
    const response = await axios.get<ApiResponse<ClubMember[]>>(
      getApiUrl(`/api/teams/${teamId}/members`),
      {
        headers: {
          'Authorization': authHeader
        }
      }
    );
    
    return response.data.data;
  } catch (error) {
    console.error(`Lỗi khi lấy danh sách thành viên của team ${teamId}:`, error);
    throw error;
  }
};

// Hàm lấy danh sách hoạt động của team
export const getTeamActivities = async (teamId: number): Promise<ClubActivity[]> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xem hoạt động');
    }
    
    const response = await axios.get<ApiResponse<ClubActivity[]>>(
      getApiUrl(`/api/teams/${teamId}/activities`),
      {
        headers: {
          'Authorization': authHeader
        }
      }
    );
    
    return response.data.data;
  } catch (error) {
    console.error(`Lỗi khi lấy danh sách hoạt động của team ${teamId}:`, error);
    throw error;
  }
};

// Hàm lấy danh sách hoạt động của team trong khoảng thời gian
export const getTeamActivitiesByDateRange = async (
  teamId: number,
  startDate: Date,
  endDate: Date
): Promise<ClubActivity[]> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xem hoạt động');
    }
    
    const startDateStr = startDate.toISOString();
    const endDateStr = endDate.toISOString();
    
    const response = await axios.get<ApiResponse<ClubActivity[]>>(
      getApiUrl(`/api/teams/${teamId}/activities/range`),
      {
        headers: {
          'Authorization': authHeader
        },
        params: {
          startDate: startDateStr,
          endDate: endDateStr
        }
      }
    );
    
    return response.data.data;
  } catch (error) {
    console.error(`Lỗi khi lấy hoạt động theo khoảng thời gian của team ${teamId}:`, error);
    throw error;
  }
};

// Hàm lấy báo cáo chi tiết về hoạt động của team
export const getTeamActivityReport = async (
  teamId: number,
  startDate: Date,
  endDate: Date
): Promise<TeamReport> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn cần đăng nhập để xem báo cáo');
    }
    
    const startDateStr = startDate.toISOString().split('T')[0]; // Lấy yyyy-MM-dd
    const endDateStr = endDate.toISOString().split('T')[0]; // Lấy yyyy-MM-dd
    
    const response = await axios.get<ApiResponse<TeamReport>>(
      getApiUrl(`/api/teams/${teamId}/activities/report`),
      {
        headers: {
          'Authorization': authHeader
        },
        params: {
          startDateStr,
          endDateStr
        }
      }
    );
    
    return response.data.data;
  } catch (error) {
    console.error(`Lỗi khi lấy báo cáo hoạt động của team ${teamId}:`, error);
    throw error;
  }
};

// Hàm ánh xạ từ User của authService sang User của teamsService 
export const mapAuthUserToTeamsUser = (): User | null => {
  try {
    const authUser = getUserInfo();
    if (!authUser) {
      return null;
    }

    // Chuyển đổi từ User của authService sang User của teamsService
    return {
      id: authUser.userId ? Number(authUser.userId) : 0,
      username: authUser.username || '',
      stravaId: authUser.stravaId || null,
      roles: (authUser.roles as string[] | null) || null,
      createdAt: authUser.createdAt || null
    };
  } catch (error) {
    console.error('Lỗi khi chuyển đổi thông tin người dùng:', error);
    return null;
  }
};

// Hàm thêm thành viên vào nhóm bằng email
export const addTeamMemberByEmail = async (teamId: number, email: string): Promise<boolean> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await axios.post(getApiUrl(`/api/teams/${teamId}/members/add-by-email`), 
      { email }, 
      {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Kết quả API thêm thành viên vào nhóm:', response.data);
    
    // Kiểm tra kết quả từ response
    if (response.data && response.data.result === "SUCCESS") {
      return true;
    }
    
    throw new Error(response.data?.message || 'Không thể thêm thành viên vào nhóm');
  } catch (error) {
    console.error('Lỗi khi thêm thành viên vào nhóm:', error);
    throw error;
  }
};

// Hàm thêm thành viên vào nhóm bằng userId (API mới)
export const addTeamMemberByUserId = async (teamId: number, userId: number): Promise<boolean> => {
  try {
    const authHeader = getAuthHeader();
    if (!authHeader) {
      throw new Error('Bạn chưa đăng nhập');
    }
    
    const response = await axios.post(getApiUrl(`/api/teams/${teamId}/members`), 
      { 
        userId: userId,
        role: "MEMBER" 
      }, 
      {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json'
        }
      }
    );
    
    console.log('Kết quả API thêm thành viên vào nhóm:', response.data);
    
    // Kiểm tra kết quả từ response
    if (response.data && response.data.result === "SUCCESS") {
      return true;
    }
    
    throw new Error(response.data?.message || 'Không thể thêm thành viên vào nhóm');
  } catch (error: any) {
    console.error('Lỗi khi thêm thành viên vào nhóm:', error);
    
    // Kiểm tra xem có response từ server không và trích xuất thông báo lỗi
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      if (errorData.message) {
        throw new Error(errorData.message);
      }
    }
    
    // Nếu không có thông báo lỗi cụ thể, trả về thông báo lỗi mặc định
    throw error.message ? new Error(error.message) : new Error('Không thể thêm thành viên vào nhóm');
  }
}; 